import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RequisitionDetailViewService,
  RequisitionDetailView,
  TemplateInfo,
  PolicyInfo,
} from '../../../../services/requisition-detail-view.service';
import { ResourceMasterDataService } from '../../../../services/resource-master-data.service';

@Component({
  selector: 'app-requisition-detail-view',
  templateUrl: './requisition-detail-view.component.html',
  styleUrls: ['./requisition-detail-view.component.css'],
})
export class RequisitionDetailViewComponent implements OnInit {
  isLoading = true;
  errorMessage: string | null = null;
  detailView: RequisitionDetailView | null = null;
  showForwardPopup = false;
  private readonly minItemsForScrollableList = 2;
  private readonly resourceTypeAliases: Record<string, string> = {
    vm: 'vm',
    ua: 'ua',
    acc: 'ua',
  };
  private readonly templateLabelMap: Record<string, string> = {
    vm: 'VM Template',
    ua: 'UA Template',
  };
  private readonly resourceTypeIconMap: Record<string, string> = {
    vm: 'computer',
    ua: 'manage_accounts',
  };
  private readonly policyLabelMap: Record<string, string> = {
    vm: 'VM Policy',
    ua: 'UA Policy',
  };

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly detailViewService: RequisitionDetailViewService,
    private readonly masterData: ResourceMasterDataService,
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.params['id'];
    this.detailViewService.getDetailView(id).subscribe({
      next: (result) => {
        this.detailView = result;
        this.isLoading = false;
      },
      error: (err) => {
        this.errorMessage = err?.error?.message ?? err?.message ?? 'Failed to load requisition details.';
        this.isLoading = false;
      },
    });
  }

  close(): void {
    this.router.navigate(['/requisitions']);
  }

  openForwardPopup(): void {
    this.showForwardPopup = true;
  }

  onForwarded(_result: any): void {
    this.showForwardPopup = false;
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  /** Maps software category keys to their master-data lookup keys. */
  private readonly categoryMasterKeys: Record<string, string> = {
    sdks: 'software.sdks',
    databases: 'software.databases',
    editors: 'software.editors',
    misc: 'software.misc',
  };

  /**
   * Formats a raw software ID into a more readable form when master-data lookup fails.
   * Replaces hyphens/underscores with spaces and capitalises the first letter of each word.
   * Example: "java-jdk-21" → "Java Jdk 21".
   */
  private formatSoftwareId(id: string): string {
    if (!id) return id;
    return id
      .replace(/[-_]/g, ' ')
      .replace(/\b[a-z]/g, c => c.toUpperCase());
  }

  /**
   * Helper to resolve a software ID to its human-readable name from master data.
   * Falls back to a prettified version of the raw ID if the name is not found.
   */
  private getSoftwareName(category: string, softwareId: string): string {
    const masterKey = this.categoryMasterKeys[category] ?? '';
    if (!masterKey) return this.formatSoftwareId(softwareId);
    const items = this.masterData.get<{ id: string; name: string }[]>(masterKey) ?? [];
    return items.find(item => item.id === softwareId)?.name ?? this.formatSoftwareId(softwareId);
  }

  /** Resource type display labels for the paired resource-group blocks. */
  private readonly resourceTypeLabelMap: Record<string, string> = {
    vm: 'VM',
    ua: 'User Account',
  };

  /**
   * Pairs each template with its matched policy (via policyTemplateId) and
   * a human-readable type label, for use in the resource-configurations section.
   */
  get resourceGroups(): { template: TemplateInfo; policy: PolicyInfo | null; typeLabel: string }[] {
    return (this.detailView?.templates ?? []).map(template => {
      const normalizedType = this.normalizeResourceType(template.resourceType);
      return {
        template,
        policy: (this.detailView?.policies ?? []).find(p => p.id === template.policyTemplateId) ?? null,
        typeLabel: this.resourceTypeLabelMap[normalizedType] ?? (template.resourceType?.toUpperCase() || 'Resource'),
      };
    });
  }

  get hasTemplates(): boolean {
    return (this.detailView?.templates?.length ?? 0) > 0;
  }

  get hasPolicies(): boolean {
    return (this.detailView?.policies?.length ?? 0) > 0;
  }

  get shouldUseScrollableTemplateList(): boolean {
    return (this.detailView?.templates?.length ?? 0) > this.minItemsForScrollableList;
  }

  get shouldUseScrollablePolicyList(): boolean {
    return (this.detailView?.policies?.length ?? 0) > this.minItemsForScrollableList;
  }

  get classStatus(): string {
    if (!this.detailView?.class) return '';
    const { status, stage } = this.detailView.class;
    return stage ? `${status} · ${stage}` : status;
  }

  /**
   * Normalized schedule stage of the class (upcoming / ongoing / completed).
   * Maps from `ClassInfo.status` — the API uses `status` to hold the temporal stage.
   * Used to pick the dot colour on the class badge.
   */
  get classStageKey(): string {
    return this.detailView?.class?.status?.toLowerCase() ?? '';
  }

  /**
   * Normalized confirmation state of the class (confirm / pending / cancelled, etc.).
   * Maps from `ClassInfo.stage` — the API uses `stage` to hold the workflow confirmation state.
   * Used to pick the border colour on the class badge.
   */
  get classConfirmKey(): string {
    return this.detailView?.class?.stage?.toLowerCase() ?? '';
  }

  hasSoftware(t: TemplateInfo | null): boolean {
    if (!t) return false;
    return (t.sdkCount + t.dbCount + t.editorCount + t.miscCount) > 0;
  }

  // ── Master-data humanizers (mirror resource-cards logic) ────────────────────

  getProviderName(t: TemplateInfo): string {
    if (!t.cloudProvider) return '';
    return this.masterData.get<string>(`cloud.${t.cloudProvider}.name`) ?? t.cloudProvider;
  }

  getRegionName(t: TemplateInfo): string {
    if (!t.cloudProvider || !t.region) return '';
    const regions = this.masterData.get<{ id: string; name: string }[]>(`cloud.${t.cloudProvider}.regions`) ?? [];
    return regions.find(r => r.id === t.region)?.name ?? t.region;
  }

  getSizeName(t: TemplateInfo): string {
    if (!t.cloudProvider || !t.size) return '';
    const sizes = this.masterData.get<{ id: string; name: string }[]>(`cloud.${t.cloudProvider}.sizes`) ?? [];
    return sizes.find(s => s.id === t.size)?.name ?? t.size;
  }

  getOsName(t: TemplateInfo): string {
    if (!t.os) return '';
    if (!t.osVersion) return t.os;
    const versions = this.masterData.get<{ id: string; name: string }[]>(`software.os.${t.os}.versions`) ?? [];
    return versions.find(v => v.id === t.osVersion)?.name ?? t.osVersion;
  }

  formatTrainers(trainers: string[]): string {
    return trainers?.length ? trainers.join(', ') : '—';
  }

  getTemplateHeading(template: TemplateInfo): string {
    const normalizedType = this.normalizeResourceType(template.resourceType);
    return this.templateLabelMap[normalizedType] ?? 'Template';
  }

  getTemplateIcon(template: TemplateInfo): string {
    const normalizedType = this.normalizeResourceType(template.resourceType);
    return this.resourceTypeIconMap[normalizedType] ?? 'inventory_2';
  }

  isVmTemplate(template: TemplateInfo): boolean {
    return this.normalizeResourceType(template.resourceType) === 'vm';
  }

  isUaTemplate(template: TemplateInfo): boolean {
    return this.normalizeResourceType(template.resourceType) === 'ua';
  }

  isGenericTemplate(template: TemplateInfo): boolean {
    return !this.isVmTemplate(template) && !this.isUaTemplate(template);
  }

  getPolicyHeading(policy: PolicyInfo): string {
    const normalizedType = this.normalizeResourceType(policy.resourceType);
    return this.policyLabelMap[normalizedType] ?? `${(policy.resourceType || 'Resource').toUpperCase()} Policy`;
  }

  getPolicyIcon(policy: PolicyInfo): string {
    const normalizedType = this.normalizeResourceType(policy.resourceType);
    return this.resourceTypeIconMap[normalizedType] ?? 'policy';
  }

  getCreationLabel(policy: PolicyInfo): string {
    return this.normalizeResourceType(policy.resourceType) === 'vm' ? 'VM Creation' : 'Creation';
  }

  private normalizeResourceType(resourceType: string): string {
    const normalizedType = resourceType?.toLowerCase() ?? '';
    return this.resourceTypeAliases[normalizedType] ?? normalizedType;
  }

  /**
   * Generates tooltip text for software stack showing actual selected tools with full names.
   * Format: "Python 3 · Node.js | PostgreSQL · MongoDB | Git · Docker"
   * Items within each category joined by " · ", categories separated by " | ".
   * Resolves abbreviated IDs to full names from master data.
   * Only includes categories with selections.
   */
  getSoftwareStackTooltip(template: TemplateInfo | null): string {
    if (!template?.metadata) return '';
    const parts: string[] = [];

    if (template.metadata.sdks && template.metadata.sdks.length > 0) {
      const names = template.metadata.sdks.map(id => this.getSoftwareName('sdks', id));
      parts.push(names.join(' · '));
    }
    if (template.metadata.databases && template.metadata.databases.length > 0) {
      const names = template.metadata.databases.map(id => this.getSoftwareName('databases', id));
      parts.push(names.join(' · '));
    }
    if (template.metadata.editors && template.metadata.editors.length > 0) {
      const names = template.metadata.editors.map(id => this.getSoftwareName('editors', id));
      parts.push(names.join(' · '));
    }
    if (template.metadata.misc && template.metadata.misc.length > 0) {
      const names = template.metadata.misc.map(id => this.getSoftwareName('misc', id));
      parts.push(names.join(' · '));
    }

    return parts.join(' | ');
  }

  /**
   * Returns tooltip text showing selected SDKs with full names resolved from master data.
   * Format: SDK names joined by middle dot (e.g., "Python 3 · Node.js · Java")
   */
  getSdkTooltip(template: TemplateInfo | null): string {
    if (!template?.metadata?.sdks || template.metadata.sdks.length === 0) return '';
    const names = template.metadata.sdks.map(id => this.getSoftwareName('sdks', id));
    return names.join(' · ');
  }

  /**
   * Returns tooltip text showing selected Databases with full names resolved from master data.
   * Format: Database names joined by middle dot (e.g., "PostgreSQL · MongoDB · MySQL")
   */
  getDatabaseTooltip(template: TemplateInfo | null): string {
    if (!template?.metadata?.databases || template.metadata.databases.length === 0) return '';
    const names = template.metadata.databases.map(id => this.getSoftwareName('databases', id));
    return names.join(' · ');
  }

  /**
   * Returns tooltip text showing selected Editors with full names resolved from master data.
   * Format: Editor names joined by middle dot (e.g., "VS Code · Visual Studio · Sublime")
   */
  getEditorTooltip(template: TemplateInfo | null): string {
    if (!template?.metadata?.editors || template.metadata.editors.length === 0) return '';
    const names = template.metadata.editors.map(id => this.getSoftwareName('editors', id));
    return names.join(' · ');
  }

  /**
   * Returns tooltip text showing selected Tools/Miscellaneous with full names resolved from master data.
   * Format: Tool names joined by middle dot (e.g., "Git · Docker · Postman · Insomnia")
   */
  getToolTooltip(template: TemplateInfo | null): string {
    if (!template?.metadata?.misc || template.metadata.misc.length === 0) return '';
    const names = template.metadata.misc.map(id => this.getSoftwareName('misc', id));
    return names.join(' · ');
  }
}
